import React, { useState, useEffect, useRef } from "react";
import { Button, Menu, MenuItem } from "@mui/material";
import { createTheme, ThemeProvider, Container } from "@mui/material";
import Link from "@mui/material/Link";
import logoWhite from "../../images/logo-white.svg";
import logoBlue from "../../images/logo-blue.png";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import { isTokenExpired, removeToken } from "../../utils/Helper";
import { useNavigate } from "react-router-dom";
import usePersistentPayerCode from "../../hooks/usePersistentPayerCode";
import aaNeelLogo from "../../images/aaneel-logo.svg";
import aaNeelLogoWhite from "../../images/aaneel-white-logo.svg";
import "./HeaderTemplate.scss";

const theme = createTheme({
    breakpoints: {
        values: {
            xs: 0,
            sm: 576,
            md: 768,
            lg: 992,
            xl: 1620,
        },
    },
});

type HeaderTemplateProps = {
    variant?: "home" | "page";
};

const HeaderTemplate: React.FC<HeaderTemplateProps> = ({
    variant = "home",
}) => {
    const navigate = useNavigate();
    const [showNavbar, setShowNavbar] = React.useState(false);
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const [isAtTop, setIsAtTop] = useState(true);
    const [scroll, setScroll] = useState(false);
    const isPageHeader = variant === "page";
    const isStickyHeader = isPageHeader || scroll;
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const [firstName] = useState(
        localStorage.getItem("firstName") || ""
    );
    const [lastName] = useState(
        localStorage.getItem("lastName") || ""
    );
    const open = Boolean(anchorEl);

    const buttonRef = useRef<HTMLButtonElement>(null);
    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };
    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleShowNavbar = () => {
        setShowNavbar(!showNavbar);
    };


    const scrollToTopSmoothly = () => {
        let scrollStep = -window.scrollY / (500 / 15), // 500ms duration, 15ms interval
            scrollInterval = setInterval(() => {
                if (window.scrollY !== 0) {
                    window.scrollBy(0, scrollStep);
                } else {
                    clearInterval(scrollInterval);
                }
            }, 15);
    };

    const handleHomeClick = (
        event: React.MouseEvent<HTMLAnchorElement, MouseEvent>
    ) => {
        if (isPageHeader) {
            return;
        }
        if (isAtTop) {
            event.preventDefault();
        } else {
            event.preventDefault();
            scrollToTopSmoothly();
        }
    };
    usePersistentPayerCode();


    useEffect(() => {
        const handleScroll = () => {
            setIsAtTop(window.scrollY === 0);
            setScroll(window.scrollY > 50);
        };

        window.addEventListener("scroll", handleScroll);

        // Initial check
        handleScroll();

        // Clean up the event listener on component unmount
        return () => {
            window.removeEventListener("scroll", handleScroll);
        };
    }, []);

    useEffect(() => {
        const checkAuth = () => {
            if (isTokenExpired()) {
                removeToken();
            }
        };

        checkAuth();
    }, []);

    useEffect(() => {
        if (localStorage.getItem("userToken")) {
            setIsLoggedIn(true);
        }
    }, [isLoggedIn]);

    const handleLogout = () => {
        localStorage.removeItem("userToken");
        localStorage.removeItem("firstName");
        localStorage.removeItem("lastName");
        setIsLoggedIn(!isLoggedIn);
        handleClose();
        navigate("/home");
    };

    const firstNameInitial = firstName ? firstName.charAt(0).toUpperCase() : "";
    const lastNameInitial = lastName ? lastName.charAt(0).toUpperCase() : "";
    const userInitials = `${firstNameInitial}${lastNameInitial}`;

    const renderBrandLogo = (variant: "hero" | "sticky" | "menu") => {
        const connectLogo =
            variant === "menu" || variant === "sticky" ? logoBlue : logoWhite;
        const isHero = variant === "hero";
        const companyLogo = isHero ? aaNeelLogoWhite : aaNeelLogo;

        return (
            <Link
                href="/home"
                className={`brand-lockup brand-lockup--${variant}`}
                onClick={variant !== "menu" ? handleHomeClick : undefined}
            >
                <div className="brand-lockup__primary">
                    <img
                        src={connectLogo}
                        alt="AaNeel Connect"
                        className="brand-lockup__connect"
                    />
                    <span className="brand-lockup__version">V 0.1</span>
                </div>

                <span className="brand-lockup__divider" aria-hidden="true" />

                <div className="brand-lockup__company">
                    {!isHero && (
                        <span className="brand-lockup__label">Powered by</span>
                    )}
                    <div className="brand-lockup__secondary">
                        <img
                            src={companyLogo}
                            alt="AaNeel"
                            className="brand-lockup__aaneel"
                        />
                        <span className="brand-lockup__version">V 11</span>
                    </div>
                </div>
            </Link>
        );
    };

    return (
        <>
            <header className={isStickyHeader ? `header-sticky${isPageHeader ? " header-page" : ""}` : ""}>
                <ThemeProvider theme={theme}>
                    <Container maxWidth="xl">
                        <div className="header-box">
                            {renderBrandLogo(isStickyHeader ? "sticky" : "hero")}
                            <div className="menu-icon" onClick={handleShowNavbar}>
                                <MenuIcon />
                            </div>
                            <div
                                className={`overlay ${showNavbar && "overlay-active"}`}
                                onClick={handleShowNavbar}
                            ></div>
                            <div className={`menu nav-elements  ${showNavbar && "active"}`}>
                                <div className="menu-logo">
                                    {renderBrandLogo("menu")}
                                    <CloseIcon onClick={handleShowNavbar} />
                                </div>
                                <ul>
                                    <li>
                                        <Link
                                            href="/home"
                                            className={`active ${isAtTop ? "text-only" : ""}`}
                                            onClick={handleHomeClick}
                                        >
                                            Home
                                        </Link>
                                    </li>
                                    {isLoggedIn && (
                                        <li>
                                            <Link href="/Dashboard">Dashboard</Link>
                                        </li>
                                    )}
                                    <li
                                        onClick={() => navigate("/api-documentation")}
                                    >
                                        <Link sx={{ cursor: "pointer" }}>
                                            API Documentation
                                        </Link>
                                    </li>
                                    <li>
                                        {isLoggedIn ? (
                                            <>
                                                <Button
                                                    className="user-btn  home-page"
                                                    id="demo-positioned-button"
                                                    aria-controls={
                                                        open ? "demo-positioned-menu" : undefined
                                                    }
                                                    aria-haspopup="true"
                                                    aria-expanded={open ? "true" : undefined}
                                                    onClick={handleClick}
                                                    ref={buttonRef}
                                                >
                                                    <div className="list-info">
                                                        <div className="welcome-info">Welcome back</div>
                                                        <div className="user-info">
                                                            {firstName && firstName + " " + lastName}
                                                        </div>
                                                    </div>
                                                    <div className="user-letter">{userInitials}</div>
                                                </Button>
                                                <Menu
                                                    id="demo-positioned-menu"
                                                    aria-labelledby="demo-positioned-button"
                                                    anchorEl={anchorEl}
                                                    className="user-profile-dropdown"
                                                    open={open}
                                                    onClose={handleClose}
                                                    disableScrollLock={true} // <-- prevents scroll blocking
                                                    transformOrigin={{
                                                        horizontal: "center",
                                                        vertical: "top",
                                                    }}
                                                    anchorOrigin={{
                                                        horizontal: "center",
                                                        vertical: "bottom",
                                                    }}
                                                    PaperProps={{
                                                        elevation: 0,
                                                        sx: {
                                                            width: 200,
                                                            overflow: "visible",
                                                            filter:
                                                                "drop-shadow(0px 2px 8px rgba(0,0,0,0.32))",
                                                        },
                                                    }}
                                                >
                                                    <MenuItem
                                                        onClick={handleLogout}
                                                        className="logout-menu"
                                                    >
                                                        Logout
                                                        <i className="an_icons">ac_logout</i>
                                                    </MenuItem>
                                                </Menu>
                                            </>
                                        ) : (
                                            <Button
                                                className="sign-in"
                                                onClick={() => navigate("/userLogin")}
                                            >
                                                <i className="an_icons">ac_lock_fill</i>
                                                Sign in
                                            </Button>
                                        )}
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </Container>
                </ThemeProvider>
            </header>
        </>
    );
};

export default HeaderTemplate;
